import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { css } from "@emotion/react";
import {
  getTokenBalanceAndSubscriptionStatus,
  getSubscriptionProvider,
} from "@sharpana/auth/api_client/allauth";
import { MOBILE_NAV_HEIGHT } from "@sharpana/shared/layout";
import {
  isMobileApp,
  getMobileAppPlatform,
  MobilePlatform,
} from "@sharpana/shared/utils/web_environment";
import { notifyManageSubscription } from "@sharpana/shared/utils/webViewBridge";

const containerStyles = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: #141414;
  color: #ffffff;
`;

const buttonStyles = css`
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  background-color: rgb(103, 40, 151);
  min-width: 110px;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgb(137, 52, 202);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(157, 60, 231, 0.4);
  }
`;

const buttonStylesLogOut = css`
  ${buttonStyles}
  background-color: rgb(120 73 40);
  &:hover {
    background-color: rgb(120 73 40);
  }
  &:focus {
    outline: none;
    box-shadow: none;
  }
`;

// Eye-catching gradient CTA specifically for the Purchase button
const purchaseButtonStyles = css`
  position: relative;
  overflow: hidden;
  padding: 12px 28px;
  font-size: 1rem;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.25);
  background: linear-gradient(
    135deg,
    rgb(90, 45, 170) 0%,
    /* between dark and bright violet */ rgb(124, 58, 237) 40%,
    /* violet-600 */ rgb(79, 70, 229) 75%,
    /* indigo-600 */ rgb(37, 99, 235) 100% /* blue-600 */
  );
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 2px 2px rgba(59, 130, 246, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;

  /* Subtle moving sheen on hover */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -150%;
    width: 120%;
    height: 100%;
    background: linear-gradient(
      120deg,
      transparent 0%,
      rgba(255, 255, 255, 0.13) 50%,
      transparent 100%
    );
    transform: skewX(-20deg);
    transition: transform 0.5s ease, left 0.5s ease;
    pointer-events: none;
  }

  &:hover::after {
    left: 150%;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 6px rgba(59, 130, 246, 0.32),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
    filter: brightness(1.04);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 3px 4px rgba(59, 130, 246, 0.22);
    filter: brightness(0.98);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.33),
      0 4px 4px rgba(59, 130, 246, 0.33);
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      display: none;
    }
    transition: none;
  }
`;

const cardStyles = css`
  width: 100%;
  max-width: 520px;
  padding: 20px 22px;
  border-radius: 14px;
  background: linear-gradient(180deg, #181818, #121212);
  border: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.03);
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 2rem;
`;

const statusBadge = css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #e9e9ee;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const dot = css`
  width: 8px;
  height: 8px;
  border-radius: 50%;
`;

const greenDot = css`
  ${dot};
  background: #22c55e;
  box-shadow: 0 0 10px rgba(34, 197, 94, 0.6);
`;

const purpleDot = css`
  ${dot};
  background: rgb(157, 60, 231);
  box-shadow: 0 0 10px rgba(157, 60, 231, 0.6);
`;

const metaText = css`
  margin-left: auto;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
`;

const loadingRow = css`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const loadingAnim = css`
  display: inline-flex;
  gap: 6px;
  align-items: center;
  & > span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.6);
    animation: pulse 1.4s infinite ease-in-out;
  }
  & > span:nth-of-type(2) {
    animation-delay: 0.15s;
  }
  & > span:nth-of-type(3) {
    animation-delay: 0.3s;
  }
  @keyframes pulse {
    0%,
    80%,
    100% {
      transform: scale(0.8);
      opacity: 0.6;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const footerContainerStyles = css`
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: max(16px, env(safe-area-inset-bottom));
  width: 100%;
  max-width: 720px;
  padding: 0 16px;
  z-index: 1000;
  display: flex;
  justify-content: center;

  /* Ensure we sit above the mobile bottom nav on small screens */
  @media (max-width: 768px) {
    bottom: calc(${MOBILE_NAV_HEIGHT} + env(safe-area-inset-bottom) + 12px);
  }
`;

const footerCardInner = css`
  margin: 0; /* override any external margin for footer variant */
  width: 100%;
`;

// Reusable ManageSubscription button component (handles mobile vs web and hidden form submission)
export function ManageSubscription({ label = "Manage" }: { label?: string }) {
  const billingFormRef = useRef<HTMLFormElement | null>(null);
  const [isManaging, setIsManaging] = useState(false);
  const isInMobileApp = isMobileApp();
  const [showModal, setShowModal] = useState(false);
  const [modalProvider, setModalProvider] = useState<
    "play_store" | "app_store" | "stripe" | null
  >(null);
  const [modalMessage, setModalMessage] = useState<string>("");

  return (
    <div>
      {/* Hidden form for web redirect to Stripe billing portal */}
      {!isInMobileApp && (
        <form
          ref={billingFormRef}
          action="/api/create-stripe-billing-portal-session"
          method="POST"
          style={{ display: "none" }}
        />
      )}
      <button
        css={buttonStyles}
        onClick={async () => {
          if (isManaging) return;

          // Fetch provider to decide behavior
          try {
            const resp: any = await getSubscriptionProvider();
            const provider: string | null = resp?.provider ?? null;

            if (isInMobileApp) {
              // On mobile, keep existing behavior unless there's a platform mismatch w/ mobile store
              const platform: MobilePlatform | null = getMobileAppPlatform();
              const mismatch =
                (provider === "app_store" && platform !== "ios") ||
                (provider === "play_store" && platform !== "android") ||
                provider === "stripe";

              if (mismatch) {
                setModalProvider((provider as any) ?? null);
                setModalMessage(
                  provider === "app_store"
                    ? "Your subscription was purchased through the Apple App Store. Please manage your subscription using an iOS/Apple device. If you need assistance, contact support@mrble.ai."
                    : provider === "play_store"
                    ? "Your subscription was purchased through the Google Play Store. Please manage your subscription using an Android device. If you need assistance, contact support@mrble.ai."
                    : 'Your subscription was purchased through the web via Stripe. Please manage your subscription from the Sharpana web app. You can navigate to this page in a mobile or desktop browser (not the mobile app) and tap "Manage" to open the billing portal. If you need assistance, contact support@mrble.ai.'
                );
                setShowModal(true);
                return;
              }

              // No mismatch -> proceed with app-native manage action
              notifyManageSubscription();
              return;
            }

            // Web: if provider is a mobile store, show guidance modal instead of Stripe portal
            if (provider === "play_store" || provider === "app_store") {
              setModalProvider(provider as any);
              setModalMessage(
                provider === "app_store"
                  ? "Your subscription was purchased through the Apple App Store. Please manage it from your iOS/Apple device's App Store settings. If you need help, contact support@mrble.ai."
                  : "Your subscription was purchased through the Google Play Store. Please manage it from your Android device's Play Store settings. If you need help, contact support@mrble.ai."
              );
              setShowModal(true);
              return;
            }

            // Web + Stripe (or no active subscription): submit to Stripe portal
            setIsManaging(true);
            billingFormRef.current?.submit();
          } catch (e) {
            // On error, fallback to previous behavior
            if (isInMobileApp) {
              notifyManageSubscription();
            } else {
              setIsManaging(true);
              billingFormRef.current?.submit();
            }
          }
        }}
        disabled={!isInMobileApp && isManaging}
      >
        {isManaging && !isInMobileApp ? "Opening…" : label}
      </button>

      {showModal &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 3000,
            }}
            role="dialog"
            aria-modal="true"
          >
            <div
              style={{
                width: "min(92vw, 520px)",
                maxHeight: "90vh",
                overflow: "auto",
                background: "#111827",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 12,
                padding: 20,
                color: "#e5e7eb",
              }}
            >
              <h3 style={{ marginTop: 0, marginBottom: 8 }}>
                Subscription managed by{" "}
                {modalProvider === "app_store"
                  ? "Apple App Store"
                  : modalProvider === "play_store"
                  ? "Google Play Store"
                  : "Stripe (Web Portal)"}
              </h3>
              <p style={{ marginTop: 0, marginBottom: 16, color: "#cbd5e1" }}>
                {modalMessage}
              </p>
              <div
                style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
              >
                <button css={buttonStyles} onClick={() => setShowModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

function SubscriptionStatusCard() {
  const [loading, setLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] =
    useState<boolean>(false);
  const [tokenBalance, setTokenBalance] = useState<number>(0);
  // ManageSubscription handles billing interactions now; no local manage state needed here

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const resp: any = await getTokenBalanceAndSubscriptionStatus();
        if (!isMounted) return;
        setHasActiveSubscription(!!resp?.has_active_subscription);
        setTokenBalance(Number(resp?.token_balance ?? 0));
      } catch (e) {
        // noop
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div css={footerContainerStyles}>
        <div css={[cardStyles, footerCardInner]}>
          <div css={statusBadge}>
            <span css={purpleDot} />
            Checking status
          </div>
          <div css={metaText}>
            <span css={loadingAnim}>
              <span />
              <span />
              <span />
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div css={footerContainerStyles}>
      <div css={[cardStyles, footerCardInner]}>
        <div css={statusBadge}>
          {hasActiveSubscription ? (
            <span css={greenDot} />
          ) : (
            <span css={purpleDot} />
          )}
          {hasActiveSubscription
            ? "Sharpana subscription active"
            : "Using tokens"}
        </div>
        {!hasActiveSubscription ? (
          <div css={metaText}>Token balance: {tokenBalance}</div>
        ) : (
          <div css={metaText}>
            <ManageSubscription />
          </div>
        )}
      </div>
    </div>
  );
}

export function Settings() {
  const navigate = useNavigate();

  const handleLogout = () => {
    navigate("/account/logout");
  };

  const goToPurchase = () => {
    navigate("/purchase");
  };

  const goToAccount = () => {
    navigate("/account");
  };

  const goToSupport = () => {
    navigate("/support");
  };

  return (
    <div css={containerStyles}>
      <br />
      <button css={purchaseButtonStyles} onClick={goToPurchase}>
        Purchase
      </button>
      <br />
      <button css={buttonStyles} onClick={goToAccount}>
        Account
      </button>
      <br />
      <button css={buttonStyles} onClick={goToSupport}>
        Support
      </button>
      <br />
      <button css={buttonStylesLogOut} onClick={handleLogout}>
        Log out
      </button>

      <SubscriptionStatusCard />
    </div>
  );
}

export default Settings;
